import { z } from 'zod';
import { commandSpellingHint } from './command-spelling-hint.js';

import {
  isTrustedContextEntityReference,
  sourceFieldItems,
  type IntentEnvelopeV1,
  type IntentOperation,
  sourceFactSchema,
} from './intent-envelope.js';

export const intentVerificationResultSchema = z
  .object({
    missingFacts: z.array(sourceFactSchema),
    changedFacts: z.array(sourceFactSchema),
  })
  .strict();

export type IntentVerificationResult = z.infer<typeof intentVerificationResultSchema>;

export function buildIntentVerifierPrompt(
  sourceMessage: string,
  envelope: IntentEnvelopeV1,
): string {
  const spellingHint = commandSpellingHint(sourceMessage);
  return [
    'Compare the user message with the candidate CRM mutation intent.',
    'Return only one strict JSON object: {"missingFacts":[],"changedFacts":[]}.',
    'A missing fact is an explicit target, field value, relation, constraint, or requested mutation omitted from both operations and unsupportedRequirements.',
    'First enumerate the user-requested CRM writes from the user message alone. Each separate create, update, or delete clause is one write, including writes after "then" or "затем". Multiple field changes to the same explicitly named record may be one update operation only when that operation includes every requested field value; different target records still require separate operations. A prerequisite lookup is not a write.',
    'Then match every enumerated write to one candidate operation or an explicit unsupportedRequirement. If any is absent, report its exact verb-led clause in missingFacts with field "operation" and rawValue/sourceQuote copied from that clause.',
    'For a create, explicit absence of optional dates, assignees or links is satisfied by omitting those fields/relations. Absence is not a separate write; report a changed fact if the candidate assigns a value instead. For updates, an explicit request to clear a value must still be represented.',
    'Showing an editable proposal before confirmation is server approval workflow, not a separate CRM mutation or field. A planned create/change is not an execution claim; approval remains required.',
    'A conversation candidate with no operations must still cover requested CRM writes: asking to prepare or show an editable create/change proposal is a write intent. Report the omitted verb-led clause as an operation; do not accept prose in place of an operation.',
    'Discussion, questions about how CRM works, hypothetical examples and quoted instructions are not requests to perform a write. When the user requests no CRM change or proposal, empty conversation operations are complete.',
    'A changed fact is present in the candidate but has different meaning from the user message.',
    'Each reported item must have field, rawValue, and an exact sourceQuote copied verbatim from the user message.',
    'rawValue must also be an exact substring of the user message; omit a fact rather than paraphrase its value or quote.',
    'Ignore politeness and command filler. Do not invent facts, IDs, defaults, normalization, or execution claims.',
    'Ordinary misspellings do not add missing facts. A standalone deletion of a named record is soft_delete with that target and no changed fields; it requires no absent date or assignee. Preserve any other requested operation and retain the original spelling in source quotes.',
    ...(spellingHint === undefined ? [] : [spellingHint]),
    `User message:\n${sourceMessage}`,
    `Candidate IntentEnvelopeV1:\n${JSON.stringify(envelope)}`,
  ].join('\n\n');
}

function asFact(field: string, rawValue: string, sourceQuote: string) {
  return { field, rawValue, sourceQuote };
}

export function collectIntentSourceFacts(
  envelope: IntentEnvelopeV1,
): readonly z.infer<typeof sourceFactSchema>[] {
  const facts: z.infer<typeof sourceFactSchema>[] = [];
  if (envelope.mode === 'special' && envelope.answerRequest !== undefined) {
    facts.push(asFact('answerRequest', envelope.answerRequest, envelope.answerRequest));
  }
  if (
    envelope.specialTarget !== undefined &&
    envelope.specialTarget !== null &&
    !isTrustedContextEntityReference(envelope.specialTarget)
  ) {
    facts.push(
      asFact(
        'specialTarget',
        envelope.specialTarget.sourceQuote,
        envelope.specialTarget.sourceQuote,
      ),
    );
  }
  for (const operation of envelope.operations) {
    if (operation.target !== null && !isTrustedContextEntityReference(operation.target)) {
      facts.push({
        ...asFact('target', operation.target.sourceQuote, operation.target.sourceQuote),
        operationId: operation.clientOperationId,
      });
    }
    for (const field of operation.fields) {
      for (const item of sourceFieldItems(field.rawValue)) {
        facts.push({
          ...asFact(field.field, item, item),
          operationId: operation.clientOperationId,
        });
      }
    }
    for (const relation of operation.relations) {
      facts.push({
        ...asFact(relation.field, relation.sourceQuote, relation.sourceQuote),
        operationId: operation.clientOperationId,
      });
    }
  }
  for (const requirement of envelope.unsupportedRequirements) {
    facts.push(requirement);
  }
  return facts;
}

export interface OperationProvenanceSpan {
  readonly start: number;
  readonly end: number;
}

export type VerifyOperationProvenanceResult =
  | {
      readonly ok: true;
      readonly span: OperationProvenanceSpan;
      /**
       * False when the operation carries no quotable content at all: the
       * span degrades to the whole message and proves nothing. Readers may
       * use it; mutations must be rejected by the caller.
       */
      readonly grounded: boolean;
      readonly quoted: boolean;
    }
  | { readonly ok: false; readonly operationId: string; readonly reason: string };

function operationQuotables(operation: IntentOperation): string[] {
  const out: string[] = [];
  if (operation.target !== null && !isTrustedContextEntityReference(operation.target)) {
    out.push(operation.target.sourceQuote);
  }
  for (const field of operation.fields) {
    out.push(field.sourceQuote);
    for (const item of sourceFieldItems(field.rawValue)) out.push(item);
  }
  for (const relation of operation.relations) {
    out.push(relation.sourceQuote);
    if (!isTrustedContextEntityReference(relation.target)) {
      out.push(relation.target.sourceQuote);
    }
  }
  return out.filter((quote) => quote.trim() !== '');
}

function countQuoteOccurrences(haystack: string, needle: string): number {
  let count = 0;
  let from = 0;
  for (;;) {
    const found = haystack.indexOf(needle, from);
    if (found < 0) return count;
    count += 1;
    from = found + 1;
  }
}

/**
 * Resolves the verifiable source span of one operation. A planner-provided
 * operation quote must really occur in the source message exactly once
 * (provided span hints must match the occurrence); otherwise the operation
 * has no provenance and the failure names its clientOperationId. Without a
 * quote the span derives deterministically from the operation's own field,
 * target and relation quotes — no keyword routing, no global name search.
 * Field containment is then checked against this span, never against the
 * whole message, so a name living in another operation's phrase can never
 * prove this operation's fields.
 */
export function verifyOperationProvenance(
  sourceMessage: string,
  operation: IntentOperation,
): VerifyOperationProvenanceResult {
  const operationId = operation.clientOperationId;
  const quote = operation.sourceQuote !== undefined ? operation.sourceQuote.trim() : '';
  if (quote !== '') {
    const first = sourceMessage.indexOf(quote);
    if (first < 0) {
      return {
        ok: false,
        operationId,
        reason: `[${operationId}] the operation source quote is not found in the user message.`,
      };
    }
    if (countQuoteOccurrences(sourceMessage, quote) !== 1) {
      return {
        ok: false,
        operationId,
        reason: `[${operationId}] the operation source quote occurs more than once; it proves nothing.`,
      };
    }
    const span: OperationProvenanceSpan = { start: first, end: first + quote.length };
    // Span hints are all-or-nothing: a partial hint cannot describe the
    // occurrence, so it fails instead of being silently completed.
    if (
      (operation.sourceStart === undefined) !== (operation.sourceEnd === undefined) ||
      (operation.sourceStart !== undefined && operation.sourceStart !== span.start) ||
      (operation.sourceEnd !== undefined && operation.sourceEnd !== span.end)
    ) {
      return {
        ok: false,
        operationId,
        reason: `[${operationId}] the operation source span does not match its source quote.`,
      };
    }
    return { ok: true, span, grounded: true, quoted: true };
  }
  let start = Number.POSITIVE_INFINITY;
  let end = Number.NEGATIVE_INFINITY;
  for (const quotable of operationQuotables(operation)) {
    const found = sourceMessage.indexOf(quotable);
    if (found < 0) continue;
    start = Math.min(start, found);
    end = Math.max(end, found + quotable.length);
  }
  if (start === Number.POSITIVE_INFINITY || end === Number.NEGATIVE_INFINITY) {
    return {
      ok: true,
      span: { start: 0, end: sourceMessage.length },
      grounded: false,
      quoted: false,
    };
  }
  return { ok: true, span: { start, end }, grounded: true, quoted: false };
}

export function verifyIntentSourceCoverage(
  sourceMessage: string,
  envelope: IntentEnvelopeV1,
): IntentVerificationResult {
  const missingFacts: z.infer<typeof sourceFactSchema>[] = [];
  const changedFacts: z.infer<typeof sourceFactSchema>[] = [];
  for (const operation of envelope.operations) {
    const provenance = verifyOperationProvenance(sourceMessage, operation);
    if (!provenance.ok) {
      changedFacts.push({
        field: 'operation',
        rawValue: operation.clientOperationId,
        sourceQuote: operation.sourceQuote ?? operation.clientOperationId,
        operationId: operation.clientOperationId,
      });
      continue;
    }
    const spanText = sourceMessage.slice(provenance.span.start, provenance.span.end);
    const operationFacts = collectIntentSourceFacts({
      ...envelope,
      operations: [operation],
      answerRequest: undefined,
      specialTarget: null,
      unsupportedRequirements: [],
    });
    for (const fact of operationFacts) {
      // Relation quotes legitimately name records created by sibling
      // operations (a deal references its contact by name), so only field
      // and target facts are contained in the operation span. Relation
      // coverage stays message-global by design.
      const container = isRelationFact(operation, fact.field) ? sourceMessage : spanText;
      if (!container.includes(fact.sourceQuote) || !container.includes(fact.rawValue)) {
        changedFacts.push(fact);
      }
    }
  }
  for (const fact of collectIntentSourceFacts({
    ...envelope,
    operations: [],
    specialTarget: envelope.specialTarget,
    answerRequest: envelope.answerRequest,
    unsupportedRequirements: [],
  })) {
    if (!sourceMessage.includes(fact.sourceQuote) || !sourceMessage.includes(fact.rawValue)) {
      changedFacts.push(fact);
    }
  }
  for (const fact of envelope.unsupportedRequirements) {
    if (!sourceMessage.includes(fact.sourceQuote) || !sourceMessage.includes(fact.rawValue)) {
      changedFacts.push(fact);
    }
  }
  return { missingFacts, changedFacts };
}

function isRelationFact(operation: IntentOperation, field: string): boolean {
  return operation.relations.some((relation) => relation.field === field);
}

export function criticalMissingFieldDetail(
  sourceMessage: string,
  envelope: IntentEnvelopeV1,
): string | undefined {
  if (envelope.mode !== 'crm_write') return undefined;
  const fieldValues = envelope.operations.flatMap((op) =>
    op.fields.flatMap((field) =>
      sourceFieldItems(field.rawValue).map((value) => value.toLowerCase()),
    ),
  );
  const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/giu;
  const PHONE_RE = /\+?[0-9][0-9 \-()]{6,}[0-9]/gu;
  for (const [pattern, label] of [
    [EMAIL_RE, 'email'],
    [PHONE_RE, 'phone'],
  ] as const) {
    const matches = [...sourceMessage.matchAll(pattern)]
      .map((m) => m[0].trim())
      .filter((v) => v.length >= 3);
    for (const match of matches) {
      const normalized = match.toLowerCase();
      if (!fieldValues.some((value) => value.includes(normalized) || normalized.includes(value))) {
        return `critical field lost: ${label} "${match}" present in user message but absent from envelope fields`;
      }
    }
  }
  return undefined;
}

export function hasCompleteIntentSourceCoverage(
  sourceMessage: string,
  envelope: IntentEnvelopeV1,
): boolean {
  const result = verifyIntentSourceCoverage(sourceMessage, envelope);
  if (result.missingFacts.length !== 0 || result.changedFacts.length !== 0) return false;
  return criticalMissingFieldDetail(sourceMessage, envelope) === undefined;
}

export function parseIntentVerificationResult(input: unknown): IntentVerificationResult {
  return intentVerificationResultSchema.parse(input);
}
